import { useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../ui/Icon';
import { Badge, Button, Card, EmptyPanel } from '../ui/primitives';
import { cx, type Tone } from '../ui/primitives/cx';
import { DenseList, DenseRow } from '../ui/DenseList';
import { StatSummary } from '../ui/StatSummary';
import type { BarView, CoverageBarView, SectionView, StatView } from '../../utils/reports/shared';
import { ReportsTile } from './ReportsParts';

/**
 * P-084 · Las piezas que comparten los cinco reportes nuevos (R3–R7). Solo dibujan lo que sale de las vistas puras
 * (`utils/reports/*View.ts`): ninguna cifra se calcula ni se formatea aquí. Reutilizan los patrones aprobados de
 * P-076 (StatSummary para 2–4 cifras, DenseList/DenseRow para listas en el teléfono) y las piezas de R1/R2.
 */

/** Las 2–4 cifras que responden la pregunta, dos por renglón en el teléfono. */
export function ReportsStats({ stats }: { stats: StatView[] }) {
  return (
    <StatSummary
      className="n3-reports__stats"
      label="Cifras del reporte"
      plain
      stats={stats.map((stat) => ({ key: stat.key, label: stat.label, value: stat.value, hint: stat.hint ?? undefined, tone: stat.tone === 'default' ? undefined : stat.tone }))}
    />
  );
}

/** Por qué no se afirma un patrón: las razones del servidor, en palabras, bajo la frase. */
export function ReportsCaution({ lines }: { lines: string[] }) {
  if (lines.length === 0) return null;
  return (
    <ul className="n3-reports__caution">
      {lines.map((line) => <li key={line}>{line}</li>)}
    </ul>
  );
}

/** El título de una tarjeta de reporte, con su mosaico. */
export function ReportsCardTitle({ id, icon, tone, children }: { id: string; icon: IconName; tone: Tone; children: ReactNode }) {
  return (
    <h2 className="n3-reports__card-title" id={id}>
      <ReportsTile icon={icon} size="sm" tone={tone} />
      <span>{children}</span>
    </h2>
  );
}

/**
 * Barras de distribución (días de la semana, horas). Es dibujo: el ancho sale de una proporción del servidor y la
 * cifra va escrita junto a cada barra; la barra marcada es la ganadora. Oculto al lector de pantalla salvo el texto.
 */
export function ReportsBars({ items, caption, label }: { items: BarView[]; caption: string; label: string }) {
  return (
    <>
      <ul aria-label={label} className="n3-reports__bars">
        {items.map((item) => (
          <li className={cx('n3-reports__bar', item.highlighted && 'n3-reports__bar--highlighted')} key={item.key}>
            <p className="n3-reports__bar-label">{item.label}</p>
            <div className="n3-reports__bar-body">
              <span aria-hidden="true" className="n3-reports__bar-track">
                {item.percent > 0 && <span className="n3-reports__bar-fill" style={{ width: `${item.percent}%` }} />}
              </span>
              <p className="n3-reports__bar-value">{item.valueLabel}</p>
            </div>
          </li>
        ))}
      </ul>
      <p className="n3-reports__caption">{caption}</p>
    </>
  );
}

/** La cobertura de costo: dos partes del total, con la leyenda en texto (K5: no es la mezcla por canal). */
export function ReportsCoverage({ view }: { view: CoverageBarView }) {
  return (
    <div className="n3-reports__proportion">
      <div aria-hidden="true" className="n3-reports__coverage-track">
        {view.knownPercent > 0 && <span className="n3-reports__coverage-known" style={{ width: `${view.knownPercent}%` }} />}
        {view.knownPercent < 100 && <span className="n3-reports__coverage-missing" />}
      </div>
      <p className="n3-reports__legend">
        <span className="n3-reports__legend-item"><span className="n3-reports__swatch n3-reports__swatch--known" />{view.knownLabel}</span>
        <span className="n3-reports__legend-item"><span className="n3-reports__swatch n3-reports__swatch--out" />{view.missingLabel}</span>
      </p>
    </div>
  );
}

/** Una sección del reporte: título y lista densa (P-076), o lo que se dice cuando está vacía. */
export function ReportsSection({ section, icon, tone, onNavigate, plain = false }: { section: SectionView; icon: IconName; tone: Tone; onNavigate: (destination: string) => void; plain?: boolean }) {
  const body = (
    <>
      {section.rows.length === 0
        ? <p className="n3-reports__section-empty">{section.empty ?? 'Nada que mostrar.'}</p>
        : (
          <DenseList label={section.title} plain>
            {section.rows.map((row) => (
              <DenseRow
                actions={row.destination ? <Button onClick={() => onNavigate(row.destination!)} variant="ghost">Ver</Button> : undefined}
                badge={row.badge ? <Badge tone={row.badge.tone}>{row.badge.text}</Badge> : undefined}
                figures={row.figures}
                key={row.key}
                meta={row.meta.length > 0 ? row.meta : undefined}
                subtitle={row.subtitle ?? undefined}
                title={row.title}
                value={row.value ?? undefined}
                valueTone={row.valueTone}
              />
            ))}
          </DenseList>
        )}
      {section.note && <p className="n3-reports__panel-note">{section.note}</p>}
      {section.more && <p className="n3-reports__section-more">{section.more}</p>}
    </>
  );
  if (plain) {
    return (
      <section aria-labelledby={`reports-section-${section.key}`} className="n3-reports__section">
        <ReportsCardTitle icon={icon} id={`reports-section-${section.key}`} tone={tone}>{section.title}</ReportsCardTitle>
        {body}
      </section>
    );
  }
  return (
    <Card aria-labelledby={`reports-section-${section.key}`} as="section" className="n3-reports__section">
      <ReportsCardTitle icon={icon} id={`reports-section-${section.key}`} tone={tone}>{section.title}</ReportsCardTitle>
      {body}
    </Card>
  );
}

/** «Ver detalle»: expande lo secundario sin repetir lo de arriba (como «Ver de dónde salió» en R1). */
export function ReportsDetail({ id, children }: { id: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        aria-controls={id}
        aria-expanded={open}
        className="n3-reports__why"
        iconStart={<Icon name={open ? 'chevron-up' : 'search'} size={16} />}
        onClick={() => setOpen((value) => !value)}
        variant="tonal"
      >
        {open ? 'Ocultar detalle' : 'Ver detalle'}
      </Button>
      {open && <div className="n3-reports__detail-block" id={id}>{children}</div>}
    </>
  );
}

/** Sin datos en el periodo: la frase honesta del reporte, como tarjeta. */
export function ReportsEmpty({ title, description }: { title: string; description: string }) {
  return <Card><EmptyPanel description={description} icon="info" plain title={title} /></Card>;
}
